import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Building2,
  Briefcase,
  Heart,
  Shield,
  Zap,
  Globe,
  Lock,
  Fingerprint,
  ChevronRight
} from 'lucide-react';

// =============================================================================
// PORTAL CONFIGURATIONS
// =============================================================================

export const PORTAL_CONFIG = {
  driver: {
    title: "Driver Portal",
    subtitle: "Credential Vault Access",
    icon: Car,
    path: "/portal/driver",
    loginPath: "/xchange/login?role=driver",
    gradient: "from-neon-cyan via-blue-500 to-neon-purple",
    particleColor: "#06b6d4",
    theme: "road", // driving animation theme
    phrases: [
      "Initializing secure connection...",
      "Verifying driver credentials...",
      "Loading your credential vault...",
      "Access granted. Welcome, Driver."
    ]
  },
  provider: {
    title: "Provider Portal",
    subtitle: "Healthcare Network Hub",
    icon: Building2,
    path: "/portal/provider",
    loginPath: "/xchange/login?role=provider",
    gradient: "from-neon-green via-emerald-500 to-teal-500",
    particleColor: "#22c55e",
    theme: "medical", // medical/DNA helix animation
    phrases: [
      "Connecting to provider network...",
      "Authenticating organization...",
      "Syncing compliance data...",
      "Portal ready. Welcome, Provider."
    ]
  },
  broker: {
    title: "Broker Portal",
    subtitle: "Trust Network Command",
    icon: Briefcase,
    path: "/portal/broker",
    loginPath: "/xchange/login?role=broker",
    gradient: "from-neon-purple via-violet-500 to-pink-500",
    particleColor: "#8b5cf6",
    theme: "network", // network nodes animation
    phrases: [
      "Establishing secure tunnel...",
      "Mapping trust relationships...",
      "Loading broker dashboard...",
      "Network active. Welcome, Broker."
    ]
  },
  patient: {
    title: "Patient Portal",
    subtitle: "Care Coordination Center",
    icon: Heart,
    path: "/portal/patient",
    loginPath: "/xchange/login?role=patient",
    gradient: "from-rose-500 via-pink-500 to-red-500",
    particleColor: "#f43f5e",
    theme: "care", // heartbeat/care animation
    phrases: [
      "Securing patient data...",
      "Connecting to care network...",
      "Loading your health profile...",
      "Ready for care. Welcome."
    ]
  }
};

// =============================================================================
// WARP SPEED PARTICLES
// =============================================================================

const WarpParticle = ({ delay, angle, color }) => {
  const distance = 800 + Math.random() * 400;
  const x = Math.cos(angle) * distance;
  const y = Math.sin(angle) * distance;
  
  return (
    <motion.div
      initial={{ 
        x: 0, 
        y: 0, 
        opacity: 0,
        scale: 0 
      }}
      animate={{ 
        x: x,
        y: y,
        opacity: [0, 1, 1, 0],
        scale: [0, 1, 2, 0]
      }}
      transition={{
        duration: 1.5,
        delay: delay,
        ease: "easeOut"
      }}
      className="absolute left-1/2 top-1/2 w-1 rounded-full"
      style={{
        height: 20 + Math.random() * 30,
        background: `linear-gradient(to bottom, transparent, ${color}, transparent)`,
        transform: `rotate(${angle}rad)`
      }}
    />
  );
};

const WarpField = ({ active, color, intensity = 100 }) => {
  const particles = Array.from({ length: intensity }, (_, i) => ({
    id: i,
    angle: (i / intensity) * Math.PI * 2 + Math.random() * 0.5,
    delay: Math.random() * 0.5
  }));
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {active && particles.map(p => (
        <WarpParticle 
          key={p.id} 
          angle={p.angle} 
          delay={p.delay}
          color={color}
        />
      ))}
    </div>
  );
};

// =============================================================================
// ROAD/DRIVING ANIMATION (Driver Portal)
// =============================================================================

const RoadAnimation = ({ active }) => {
  const [lines, setLines] = useState([]);
  
  useEffect(() => {
    if (!active) return;
    
    const interval = setInterval(() => {
      setLines(prev => [
        ...prev.slice(-20),
        { id: Date.now(), x: Math.random() * 100 }
      ]);
    }, 100);
    
    return () => clearInterval(interval);
  }, [active]);
  
  return (
    <div className="absolute inset-0 overflow-hidden perspective-1000">
      {/* Horizon glow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 1 : 0 }}
        className="absolute bottom-0 left-0 right-0 h-1/2"
        style={{
          background: 'linear-gradient(to top, rgba(6, 182, 212, 0.3), transparent)'
        }}
      />
      
      {/* Road lines */}
      {lines.map(line => (
        <motion.div
          key={line.id}
          initial={{ 
            bottom: '50%',
            opacity: 0,
            scaleY: 0.1,
            left: `${line.x}%`
          }}
          animate={{ 
            bottom: '-10%',
            opacity: [0, 1, 0],
            scaleY: 1
          }}
          transition={{ duration: 0.8, ease: "easeIn" }}
          className="absolute w-1 h-20 bg-neon-cyan rounded-full"
          style={{
            boxShadow: '0 0 20px #06b6d4'
          }}
        />
      ))}
      
      {/* Speed lines on sides */}
      <motion.div
        animate={{ 
          background: active ? [
            'linear-gradient(90deg, rgba(6,182,212,0.5) 0%, transparent 50%)',
            'linear-gradient(90deg, rgba(6,182,212,0.8) 0%, transparent 30%)',
            'linear-gradient(90deg, rgba(6,182,212,0.5) 0%, transparent 50%)'
          ] : 'transparent'
        }}
        transition={{ duration: 0.5, repeat: Infinity }}
        className="absolute left-0 top-0 bottom-0 w-1/4"
      />
      <motion.div
        animate={{ 
          background: active ? [
            'linear-gradient(-90deg, rgba(6,182,212,0.5) 0%, transparent 50%)',
            'linear-gradient(-90deg, rgba(6,182,212,0.8) 0%, transparent 30%)',
            'linear-gradient(-90deg, rgba(6,182,212,0.5) 0%, transparent 50%)'
          ] : 'transparent'
        }}
        transition={{ duration: 0.5, repeat: Infinity }}
        className="absolute right-0 top-0 bottom-0 w-1/4"
      />
    </div>
  );
};

// =============================================================================
// DNA HELIX ANIMATION (Provider Portal)
// =============================================================================

const DNAHelix = ({ active }) => {
  const helixPoints = Array.from({ length: 20 }, (_, i) => i);
  
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {active && helixPoints.map((point, i) => (
        <React.Fragment key={i}>
          {/* Left strand */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              x: Math.sin(i * 0.5) * 100,
              y: (i - 10) * 30
            }}
            transition={{ 
              delay: i * 0.05,
              duration: 0.3,
              repeat: Infinity,
              repeatType: "reverse",
              repeatDelay: 1
            }}
            className="absolute w-4 h-4 rounded-full bg-neon-green"
            style={{ boxShadow: '0 0 20px #22c55e' }}
          />
          {/* Right strand */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              x: Math.sin(i * 0.5 + Math.PI) * 100,
              y: (i - 10) * 30
            }}
            transition={{ 
              delay: i * 0.05 + 0.1,
              duration: 0.3,
              repeat: Infinity,
              repeatType: "reverse",
              repeatDelay: 1
            }}
            className="absolute w-4 h-4 rounded-full bg-emerald-400"
            style={{ boxShadow: '0 0 20px #34d399' }}
          />
          {/* Connection line */}
          {i % 2 === 0 && (
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 0.5, scaleX: 1 }}
              transition={{ delay: i * 0.05 + 0.2 }}
              className="absolute h-0.5 bg-gradient-to-r from-neon-green to-emerald-400"
              style={{
                width: Math.abs(Math.sin(i * 0.5)) * 200,
                top: `calc(50% + ${(i - 10) * 30}px)`
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// =============================================================================
// NETWORK NODES ANIMATION (Broker Portal)
// =============================================================================

const NetworkNodes = ({ active }) => {
  const nodes = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.cos(i * (Math.PI * 2 / 12)) * 150,
    y: Math.sin(i * (Math.PI * 2 / 12)) * 150
  }));
  
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {/* Central hub */}
      {active && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="absolute w-20 h-20 rounded-full bg-neon-purple/30 border-2 border-neon-purple flex items-center justify-center z-10"
          style={{ boxShadow: '0 0 40px #8b5cf6' }}
        >
          <Globe className="w-10 h-10 text-neon-purple" />
        </motion.div>
      )}
      
      {/* Outer nodes */}
      {active && nodes.map((node, i) => (
        <React.Fragment key={node.id}>
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              x: node.x,
              y: node.y
            }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className="absolute w-8 h-8 rounded-full bg-violet-500/50 border border-violet-400 flex items-center justify-center"
          >
            <div className="w-2 h-2 rounded-full bg-violet-300" />
          </motion.div>
          
          {/* Connection line to center */}
          <motion.div
            initial={{ opacity: 0, pathLength: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: i * 0.1 + 0.3 }}
            className="absolute w-px bg-gradient-to-b from-neon-purple to-transparent"
            style={{
              height: 150,
              transform: `rotate(${i * 30}deg)`,
              transformOrigin: 'top center'
            }}
          />
        </React.Fragment>
      ))}
      
      {/* Pulsing data packets */}
      {active && nodes.slice(0, 6).map((node, i) => (
        <motion.div
          key={`packet-${i}`}
          initial={{ x: 0, y: 0, opacity: 0 }}
          animate={{ 
            x: [0, node.x * 0.8],
            y: [0, node.y * 0.8],
            opacity: [0, 1, 0]
          }}
          transition={{ 
            duration: 1,
            delay: i * 0.2,
            repeat: Infinity,
            repeatDelay: 1
          }}
          className="absolute w-2 h-2 rounded-full bg-pink-400"
          style={{ boxShadow: '0 0 10px #f472b6' }}
        />
      ))}
    </div>
  );
};

// =============================================================================
// HEARTBEAT/CARE ANIMATION (Patient Portal)
// =============================================================================

const HeartbeatAnimation = ({ active }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {/* ECG line */}
      {active && (
        <svg className="absolute w-full h-32" viewBox="0 0 400 100">
          <motion.path
            d="M0,50 L80,50 L100,50 L110,20 L120,80 L130,30 L140,70 L150,50 L200,50 L220,50 L230,20 L240,80 L250,30 L260,70 L270,50 L320,50 L340,50 L350,20 L360,80 L370,30 L380,70 L390,50 L400,50"
            fill="none"
            stroke="#f43f5e"
            strokeWidth="2"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ filter: 'drop-shadow(0 0 10px #f43f5e)' }}
          />
        </svg>
      )}
      
      {/* Pulsing heart */}
      {active && (
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1, 1.2, 1],
          }}
          transition={{ 
            duration: 1,
            repeat: Infinity,
            times: [0, 0.1, 0.2, 0.3, 1]
          }}
          className="absolute"
        >
          <Heart 
            className="w-32 h-32 text-rose-500" 
            fill="rgba(244, 63, 94, 0.3)"
            style={{ filter: 'drop-shadow(0 0 30px #f43f5e)' }}
          />
        </motion.div>
      )}
      
      {/* Radiating rings */}
      {active && [1, 2, 3].map(ring => (
        <motion.div
          key={ring}
          initial={{ scale: 0.5, opacity: 0.8 }}
          animate={{ scale: 2.5, opacity: 0 }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            delay: ring * 0.6
          }}
          className="absolute w-32 h-32 rounded-full border-2 border-rose-500"
        />
      ))}
    </div>
  );
};

// =============================================================================
// BIOMETRIC SCAN OVERLAY
// =============================================================================

const BiometricScan = ({ active, onComplete }) => {
  const [scanProgress, setScanProgress] = useState(0);
  
  useEffect(() => {
    if (!active) {
      setScanProgress(0);
      return;
    }
    
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 200);
          return 100;
        }
        return prev + 5;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [active, onComplete]);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      className="absolute inset-0 flex items-center justify-center z-20"
    >
      <div className="relative w-48 h-48">
        {/* Scanner frame */}
        <div className="absolute inset-0 border-4 border-neon-cyan/50 rounded-2xl">
          <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-neon-cyan rounded-tl-2xl" />
          <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-neon-cyan rounded-tr-2xl" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-neon-cyan rounded-bl-2xl" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-neon-cyan rounded-br-2xl" />
        </div>
        
        {/* Scan line */}
        <motion.div
          animate={{ top: `${scanProgress}%` }}
          className="absolute left-0 right-0 h-1 bg-neon-cyan"
          style={{ boxShadow: '0 0 20px #06b6d4, 0 0 40px #06b6d4' }}
        />
        
        {/* Fingerprint icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <Fingerprint className="w-24 h-24 text-white/30" />
        </div>
        
        {/* Progress text */}
        <div className="absolute -bottom-10 left-0 right-0 text-center">
          <span className="text-neon-cyan font-mono">{scanProgress}%</span>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// MAIN PORTAL ENTRANCE ANIMATION
// =============================================================================

export const PortalEntrance = ({ 
  portal, // 'driver' | 'provider' | 'broker' | 'patient'
  isActive,
  onComplete,
  useLoginPath = true // If true, navigate to login page; if false, navigate to dashboard
}) => {
  const [phase, setPhase] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const navigate = useNavigate();
  
  // Get config with fallback
  const config = portal ? PORTAL_CONFIG[portal] : null;
  
  useEffect(() => {
    if (!isActive || !portal || !config) {
      setPhase(0);
      setPhraseIndex(0);
      return;
    }
    
    // Phase 0: Initial - show portal icon
    // Phase 1: Biometric scan
    // Phase 2: Warp/themed animation
    // Phase 3: Final message + navigate
    
    // Determine destination path
    const destinationPath = useLoginPath ? (config.loginPath || config.path) : config.path;
    
    const timers = [
      setTimeout(() => setPhase(1), 500),    // Start biometric
      setTimeout(() => setPhase(2), 2000),   // Start warp
      setTimeout(() => setPhraseIndex(1), 2500),
      setTimeout(() => setPhraseIndex(2), 3500),
      setTimeout(() => setPhase(3), 4000),   // Final
      setTimeout(() => setPhraseIndex(3), 4200),
      setTimeout(() => {
        onComplete?.();
        navigate(destinationPath);
      }, 5000)
    ];
    
    return () => timers.forEach(clearTimeout);
  }, [isActive, portal, config, navigate, onComplete, useLoginPath]);
  
  // Early return after hooks
  if (!portal || !isActive || !config) {
    return null;
  }
  
  const Icon = config.icon;
  
  // Choose themed animation based on portal
  const ThemedAnimation = {
    road: RoadAnimation,
    medical: DNAHelix,
    network: NetworkNodes,
    care: HeartbeatAnimation
  }[config.theme];
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black"
        >
          {/* Background gradient */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 0.3 : 0 }}
            className={`absolute inset-0 bg-gradient-to-br ${config.gradient}`}
          />
          
          {/* Themed background animation */}
          <ThemedAnimation active={phase >= 2} />
          
          {/* Warp particles */}
          <WarpField 
            active={phase >= 2} 
            color={config.particleColor}
            intensity={phase >= 2 ? 150 : 0}
          />
          
          {/* Biometric scan overlay */}
          {phase === 1 && (
            <BiometricScan 
              active={true}
              onComplete={() => {}}
            />
          )}
          
          {/* Central portal icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ 
                scale: phase >= 3 ? 0 : phase >= 2 ? 1.5 : 1,
                rotate: 0,
                opacity: phase >= 3 ? 0 : 1
              }}
              transition={{ 
                type: "spring",
                damping: 15,
                stiffness: 100
              }}
              className="relative z-30"
            >
              {/* Glow rings */}
              {[1, 2, 3].map(ring => (
                <motion.div
                  key={ring}
                  initial={{ scale: 1, opacity: 0 }}
                  animate={{ 
                    scale: phase >= 2 ? [1, 2, 3] : 1,
                    opacity: phase >= 2 ? [0.5, 0.3, 0] : 0
                  }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    delay: ring * 0.3
                  }}
                  className={`absolute inset-0 rounded-full border-2`}
                  style={{ borderColor: config.particleColor }}
                />
              ))}
              
              {/* Icon container */}
              <motion.div
                animate={{
                  boxShadow: phase >= 2 
                    ? `0 0 60px ${config.particleColor}, 0 0 120px ${config.particleColor}`
                    : `0 0 30px ${config.particleColor}`
                }}
                className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center`}
              >
                <Icon className="w-16 h-16 text-white" />
              </motion.div>
            </motion.div>
          </div>
          
          {/* Status text */}
          <div className="absolute bottom-32 left-0 right-0 text-center z-30">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl font-bold text-white mb-2"
            >
              {config.title}
            </motion.h2>
            
            <motion.p
              key={phraseIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-lg font-mono"
              style={{ color: config.particleColor }}
            >
              {config.phrases[phraseIndex]}
            </motion.p>
          </div>
          
          {/* Security badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center space-x-2 text-white/50 z-30"
          >
            <Shield className="w-4 h-4" />
            <span className="text-xs font-mono">SECURE CONNECTION ESTABLISHED</span>
            <Lock className="w-4 h-4" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// PORTAL SELECTION CARD WITH ENTRANCE
// =============================================================================

export const PortalCard = ({ portal, onClick }) => {
  const config = PORTAL_CONFIG[portal];
  const Icon = config.icon;
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02, y: -5 }}
      whileTap={{ scale: 0.98 }}
      className={`
        relative group w-full p-8 rounded-2xl overflow-hidden
        bg-gradient-to-br from-slate-900 to-slate-800
        border border-white/10 hover:border-white/20
        transition-all duration-300
      `}
    >
      {/* Hover glow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 0.2 : 0 }}
        className={`absolute inset-0 bg-gradient-to-br ${config.gradient}`}
      />
      
      {/* Content */}
      <div className="relative z-10">
        <motion.div
          animate={{ 
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? 5 : 0
          }}
          className={`w-16 h-16 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mb-6`}
        >
          <Icon className="w-8 h-8 text-white" />
        </motion.div>
        
        <h3 className="text-2xl font-bold text-white mb-2">{config.title}</h3>
        <p className="text-slate-400 mb-4">{config.subtitle}</p>
        
        <div className="flex items-center text-sm" style={{ color: config.particleColor }}>
          <span>Enter Portal</span>
          <motion.div
            animate={{ x: isHovered ? 5 : 0 }}
          >
            <ChevronRight className="w-4 h-4 ml-1" />
          </motion.div>
        </div>
      </div>
      
      {/* Animated particles on hover */}
      {isHovered && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 10 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                x: Math.random() * 100 + '%',
                y: '100%',
                opacity: 0 
              }}
              animate={{ 
                y: '-20%',
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 2,
                delay: i * 0.1,
                repeat: Infinity
              }}
              className="absolute w-1 h-8 rounded-full"
              style={{ background: config.particleColor }}
            />
          ))}
        </div>
      )}
    </motion.button>
  );
};

// =============================================================================
// HOOK FOR PORTAL ENTRANCE
// =============================================================================

export const usePortalEntrance = () => {
  const [activePortal, setActivePortal] = useState(null);
  
  const enterPortal = useCallback((portal) => {
    setActivePortal(portal);
  }, []);
  
  const closePortal = useCallback(() => {
    setActivePortal(null);
  }, []);
  
  const PortalEntranceComponent = useCallback(() => (
    <PortalEntrance
      portal={activePortal}
      isActive={activePortal !== null}
      onComplete={closePortal}
    />
  ), [activePortal, closePortal]);
  
  return {
    enterPortal,
    closePortal,
    activePortal,
    PortalEntranceComponent
  };
};

export default {
  PortalEntrance,
  PortalCard,
  usePortalEntrance,
  PORTAL_CONFIG
};
